import { api } from '../api/client';
import { AsyncError, EmptyState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Pool {
  id: string;
  name: string;
  kind: string;
  family: string;
  cidr: string;
  capacity: string;
  used: number;
  utilizationPercent: number;
}

interface CapacityReport {
  pools: Pool[];
  highUtilization: number;
}

function utilizationChip(percent: number): string {
  if (percent >= 80) return 'chip--critical';
  if (percent >= 50) return 'chip--warning';
  return 'chip--online';
}

/**
 * Capacity report: address-pool utilization, so an operator sees exhaustion coming before
 * an allocation fails. Utilization is shown as a labelled percentage (not colour alone) with
 * the used/total counts, and pools at or above 80% are flagged up front.
 */
export function ReportsPage(): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync<CapacityReport>(
    () => api.get<CapacityReport>('/operations/capacity'),
    [],
  );

  return (
    <section>
      <div className="page-head">
        <h1>{t('reports.title')}</h1>
      </div>

      <div className="card">
        <h2>{t('reports.addressPools')}</h2>
        {loading ? (
          <Loading />
        ) : error ? (
          <AsyncError error={error} onRetry={reload} />
        ) : !data || data.pools.length === 0 ? (
          <EmptyState hint={t('reports.noPools')} />
        ) : (
          <>
            {data.highUtilization > 0 ? (
              <div className="banner banner--warning" role="alert">
                {t('reports.highUtilization').replace('{count}', String(data.highUtilization))}
              </div>
            ) : null}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('reports.pool')}</th>
                    <th>{t('reports.kind')}</th>
                    <th>{t('reports.cidr')}</th>
                    <th>{t('reports.used')}</th>
                    <th>{t('reports.utilization')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pools.map((pool) => (
                    <tr key={pool.id}>
                      <td>{pool.name}</td>
                      <td>{pool.kind}</td>
                      <td>
                        <code>{pool.cidr}</code>
                      </td>
                      <td>
                        {pool.used} / {pool.capacity}
                      </td>
                      <td>
                        <span className={`chip ${utilizationChip(pool.utilizationPercent)}`}>
                          {pool.utilizationPercent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
